/* Header scroll behavior remains owned by Impact. Only geometry is measured here. */
if (!customElements.get('gj-product-layout')) {
  customElements.define('gj-product-layout', class extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-media-fit')) return;

      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.scheduleMeasure = () => {
        if (!this.frame) {
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.measure();
          });
        }
      };

      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      // The product grid also changes height when accordions/app blocks update.
      this.resizeObserver.observe(this);
      for (const element of [this.previousElementSibling, document.querySelector('store-header'), document.querySelector('.shopify-section--announcement-bar')]) {
        if (element) this.resizeObserver.observe(element);
      }

      this.mutationObserver = new MutationObserver(this.scheduleMeasure);
      this.mutationObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
      window.addEventListener('resize', this.scheduleMeasure, options);
      window.visualViewport?.addEventListener('resize', this.scheduleMeasure, options);
      for (const event of ['carousel:change', 'carousel:settle', 'variant:change', 'load']) {
        this.addEventListener(event, this.scheduleMeasure, { ...options, capture: true });
      }
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.scheduleMeasure();
      });
      this.measure();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    measure() {
      if (!window.matchMedia('(min-width: 1000px)').matches) {
        this.style.removeProperty('--gj-media-size');
        this.removeAttribute('data-media-no-room');
        this.viewportSize = null;
        return;
      }

      const gallery = this.querySelector(':scope > product-gallery');
      const stage = gallery?.querySelector('.product-gallery__media-list-wrapper');
      const arWrapper = gallery?.querySelector('.product-gallery__ar-wrapper');
      if (!gallery || !stage || !arWrapper) return;

      const rootStyle = getComputedStyle(document.documentElement);
      const galleryStyle = getComputedStyle(gallery);
      const pixels = (style, name) => parseFloat(style.getPropertyValue(name)) || 0;
      const gap = pixels(getComputedStyle(this), '--gj-media-gap');
      const thumbnailWrapper = gallery.querySelector('.product-gallery__thumbnail-list-wrapper');
      const hasThumbnails = thumbnailWrapper && getComputedStyle(thumbnailWrapper).display !== 'none';
      const verticalThumbnails = hasThumbnails && gallery.classList.contains('product-gallery--desktop-thumbnails-left') && window.matchMedia('(min-width: 1150px)').matches;
      const thumbnailWidth = verticalThumbnails ? thumbnailWrapper.getBoundingClientRect().width + pixels(galleryStyle, 'column-gap') : 0;
      const thumbnailHeight = hasThumbnails && !verticalThumbnails ? thumbnailWrapper.getBoundingClientRect().height + pixels(galleryStyle, 'row-gap') : 0;
      const extraMediaHeight = Math.max(0, arWrapper.getBoundingClientRect().height - stage.getBoundingClientRect().height);
      const width = gallery.getBoundingClientRect().width - thumbnailWidth;
      const viewportSize = `${window.innerWidth}x${window.innerHeight}`;
      if (this.viewportSize !== viewportSize || window.scrollY === 0) {
        this.widthBudget = width;
        this.viewportSize = viewportSize;
      }
      // Overlay scrollbars/apps can temporarily widen the column while scrolling.
      // Keep the original image size until the viewport or top-level layout changes.
      const stableWidth = Math.min(width, this.widthBudget);

      // Use document position, not the sticky gallery's moving viewport position.
      // Reserve the visible header's height even when it is hidden so the picture
      // does not grow/shrink on every scroll-direction change.
      const documentTop = Math.max(0, this.getBoundingClientRect().top + window.scrollY);
      const visibleHeader = pixels(rootStyle, '--sticky-header-enabled') * pixels(rootStyle, '--header-height');
      const visibleAnnouncement = pixels(rootStyle, '--sticky-announcement-bar-enabled') * pixels(rootStyle, '--announcement-bar-height');
      const top = Math.max(documentTop, visibleHeader + visibleAnnouncement + gap);
      const height = window.innerHeight - top - gap - thumbnailHeight - extraMediaHeight;
      const size = Math.max(1, Math.floor(Math.min(stableWidth, height)));
      const value = `${size}px`;
      if (this.style.getPropertyValue('--gj-media-size') !== value) {
        this.style.setProperty('--gj-media-size', value);
      }
      // Very short windows still allow normal page scrolling instead of trapping media.
      this.toggleAttribute('data-media-no-room', height < 160);
    }
  });
}
